import { useCallback, useState } from "react"

import { USER_NAME } from "../config/constants"
import { getStats, getUserChallenges } from "./api"
import { formatCountdown } from "./format-countdown"
import { useCachedHistoryQuery, useCachedStatsQuery } from "./use-cached-query"

const userName = (): string=> localStorage.getItem(USER_NAME) ?? ""

const formatSeconds = (seconds: null | number): string=>
  seconds === null ? "—" : formatCountdown(seconds)

const formatRelativeTime = (createdAt: string, serverTime: Date): string=> {
  const diff = serverTime.getTime() - new Date(createdAt).getTime()
  const minutes = Math.floor(diff / 60000)
  if (minutes < 1) return "just now"
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

export function HistoryView(input: { onBack: ()=> void }) {
  const [filter, setFilter] = useState<"completed" | "pending">("completed")
  const loader = useCallback(()=> getUserChallenges(userName()), [])
  const { data, diff, failed, serverTime } = useCachedHistoryQuery(loader)
  const filtered = data?.filter(row=> {
    if (filter === "completed") return row.status === "won" || row.status === "lost"
    return row.status === "pending"
  })
  return (
    <div className="mx-auto w-full max-w-md px-4 pt-4">
      <BackButton onBack={input.onBack} />
      <h2 className="mb-2 text-xl font-bold">My games</h2>
      <div className="mb-3 flex gap-2">
        <button
          className={`
            rounded-sm px-3 py-1 text-sm
            ${filter === "completed" ? `bg-primary text-primary-foreground` : `
              bg-secondary text-secondary-foreground
            `}
          `}
          onClick={()=> setFilter("completed")}
          type="button"
        >
          Completed
        </button>
        <button
          className={`
            rounded-sm px-3 py-1 text-sm
            ${filter === "pending" ? `bg-primary text-primary-foreground` : `
              bg-secondary text-secondary-foreground
            `}
          `}
          onClick={()=> setFilter("pending")}
          type="button"
        >
          Pending
        </button>
      </div>
      {diff && <DiffBanner message={diff.message} />}
      {failed || data === null ? (
        <div>No data available</div>
      ) : filtered && filtered.length === 0 ? (
        <div>No {filter} games</div>
      ) : (
        <div className="max-h-[70vh] overflow-auto">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="text-left">#</th>
                <th className="text-left">Result</th>
                <th className="text-left">Opponent</th>
                <th className="text-left">Mode</th>
                <th className="text-right">My time</th>
                <th className="text-right">Opp time</th>
                {serverTime && <th className="text-right">When</th>}
              </tr>
            </thead>
            <tbody>
              {filtered?.map(row=> {
                const isSolo = row.opponent_name === null
                return (
                  <tr key={row.id}>
                    <td className="text-left">{row.id}</td>
                    <td className="text-left font-medium capitalize">{row.status}</td>
                    <td className="text-left">{isSolo ? "Solo" : (row.opponent_name ?? "—")}</td>
                    <td className="text-left">{row.hard_mode ? "Hard" : "Easy"}</td>
                    <td className="text-right">{formatSeconds(row.creator_time)}</td>
                    <td className="text-right">{isSolo ? "—" : formatSeconds(row.acceptor_time)}</td>
                    {serverTime && (
                      <td className="text-muted-foreground text-right">
                        {row.completed_at
                          ? formatRelativeTime(row.completed_at, serverTime)
                          : "—"}
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export function LeaderboardView(input: { onBack: ()=> void }) {
  const { data, diff, failed } = useCachedStatsQuery(getStats)
  return (
    <div className="mx-auto w-full max-w-md px-4 pt-4">
      <BackButton onBack={input.onBack} />
      <h2 className="mb-2 text-xl font-bold">Leaderboard</h2>
      {diff && <DiffBanner message={diff.message} />}
      {failed || data === null ? (
        <div>No data available</div>
      ) : data.length === 0 ? (
        <div>No data available</div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th className="text-left">#</th>
              <th className="text-left">Name</th>
              <th className="text-right">Win%</th>
              <th className="text-right">W</th>
              <th className="text-right">L</th>
              <th className="text-right">Games</th>
              <th className="text-right">Streak</th>
              <th className="text-right">Diff</th>
              <th className="text-right">Hints</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, index)=> (
              <tr key={row.id}>
                <td className="text-left">{index + 1}</td>
                <td className="text-left">{row.name}</td>
                <td className="text-right">{row.win_pct}</td>
                <td className="text-right">{row.wins}</td>
                <td className="text-right">{row.losses}</td>
                <td className="text-right">{row.total_games}</td>
                <td className="text-right">{row.current_streak}</td>
                <td className="text-right">{row.total_diff}</td>
                <td className="text-right">{row.avg_hints}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

function BackButton(input: { onBack: ()=> void }) {
  return (
    <button
      className="mb-4 text-sm text-secondary-foreground underline"
      onClick={input.onBack}
      type="button"
    >
      ← Back
    </button>
  )
}

function DiffBanner(input: { message: string }) {
  return (
    <div className="
      mb-2 rounded-md bg-blue-50 px-3 py-2 text-xs text-blue-700
      dark:bg-blue-950 dark:text-blue-300
    ">
      {input.message}
    </div>
  )
}
